import { useState, useEffect, useRef, useSyncExternalStore } from "react";
import "./MessageText.css";
// import parse from "html-react-parser";
import DOMPurify from "dompurify";
import { marked } from "marked";
import { v4 as uuidv4 } from "uuid";
import AddyWebSocket from "../models/addyWebSocket";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ComparisonModal from "./ComparisonModal";
import { Menu, MenuItem, Skeleton, CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import {AttachmentContainer} from "./AttachmentContainer";
import CitationPopover from "./CitationPopover";
import {
  CITATION_PILL_CLASS,
  getCitationTargetOrigin,
  insertCitationPills,
  isPdfCitation,
  normalizeCitationMarkers,
  stripCitationMarkers,
} from "../helpers/citations";
import {
  resolveLinkReferences,
  deriveLinkLabelFromUrl,
} from "../utils/linkReferences";
import {
  AGENT_LINK_URI_REGEXP,
  GUIDE_BUTTON_CLASS,
  NAVIGATE_LINK_SCHEME,
  findHelpArticleUrl,
  isGuideAvailable,
  isGuideHref,
  navigateFromLink,
  parseGuideHref,
  parseNavigateHref,
  placeGuideButtons,
  prefetchGuidedWalkthrough,
  startGuidedWalkthroughFromLink,
  subscribeGuideAvailability,
} from "../utils/guidedWalkthrough";
import {
  copyRenderedMessage,
  getRenderedPlainText,
} from "../utils/copyMessageContent";
import GlobalVariables from "../models/globalVariables";
import PricingResultsCard from "./PricingResultsCard";
import FilePreview from "./FilePreview";
import {
  buildPreviewFileFromLink,
  canPreviewDocumentLink,
} from "../utils/documentPreviewHelpers";

function getHostAppOrigin() {
  if (!window.parent || window.parent === window) {
    return "";
  }

  return getCitationTargetOrigin();
}

// Only the Addy web app mounts the full-page viewer; anywhere else (Gmail, the
// help center) the preview opens inside the chat itself.
function openDocumentPreview(previewFile, setLocalPreviewDocument) {
  const hostAppOrigin = getHostAppOrigin();
  if (hostAppOrigin) {
    window.parent.postMessage(
      { key: "open-document-preview", value: previewFile },
      hostAppOrigin
    );

    return;
  }

  setLocalPreviewDocument(previewFile);
}
// Get current window width

function shuffleArray(array) {
  return array
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
}

// How long a citation quick view stays after the pointer leaves the pill, so
// the pointer can move onto the card and its "See in Document" button.
const CITATION_HIDE_DELAY_MS = 400;

const MessageText = (props) => {
  // A finished answer with a "Show me on screen" button: let the app plan the
  // first step while the user reads, so the guide starts without a wait.
  const guideMessageText = props.message?.type === "user" ? "" : props.message?.message || "";
  // No guide at mobile size or in the mobile app: its buttons are left out.
  const guideAvailable = useSyncExternalStore(subscribeGuideAvailability, isGuideAvailable);
  useEffect(() => {
    if (!guideAvailable || props.showTypingCursor || !guideMessageText.includes("(guide:")) return;
    const hostWindow = window.parent && window.parent !== window ? window.parent : null;
    prefetchGuidedWalkthrough({
      messageText: guideMessageText,
      goal: props.previousUserMessage || "",
      parentWindow: hostWindow,
    });
  }, [guideAvailable, guideMessageText, props.showTypingCursor, props.previousUserMessage]);

  // const [likeSrc, setLikeSrc] = useState("/thumb.svg");
  //   const [dislikeSrc, setDislikeSrc] = useState("/thumb-1.svg");
  const feedbackContainerRef = useRef(null);
  // The rendered response node, so Copy serializes clean hyperlinks (rendered
  // DOM) instead of the raw Markdown, which prints full signed URLs inline.
  const responseTextRef = useRef(null);
  // const [messageText, setMessageText] = useState(props.message.message);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );
  const [showMoreSources, setShowMoreSources] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openTooltipIndex, setOpenTooltipIndex] = useState(null);
  const [expandedSourceIndexes, setExpandedSourceIndexes] = useState({});
  const [exportMenuAnchorEl, setExportMenuAnchorEl] = useState(null);
  const [exportingFormat, setExportingFormat] = useState(null);
  const [exportError, setExportError] = useState(false);
  const [copySucceeded, setCopySucceeded] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Set up the resize event listener
    window.addEventListener("resize", handleResize);

    // Clean up function
    return () => {
      // Remove the resize event listener
      window.removeEventListener("resize", handleResize);
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount

  const message = props.message;
  const documents = props.documents;
  const pricingResults = props.pricingResults;
  const organizationLogoUrl =
    typeof props.organizationLogoUrl === "string" ? props.organizationLogoUrl.trim() : "";
  const isDefaultMessageAvatar =
    message?.imageURL === "https://i.imgur.com/W1yNbq7.png" ||
    message?.imageURL === "/assistant-icon.svg";
  const messageImageUrl =
    organizationLogoUrl && isDefaultMessageAvatar ? organizationLogoUrl : message?.imageURL;
  const normalizedMessageImageUrl =
    typeof messageImageUrl === "string" ? messageImageUrl.trim() : "";
  const [avatarImageFailed, setAvatarImageFailed] = useState(false);
  // Chat with your docs (DEV-6130): the citation pill whose quick view is open.
  const citations = Array.isArray(message?.citations) ? message.citations : [];
  const [activeCitation, setActiveCitation] = useState(null);
  const citationHideTimerRef = useRef(null);

  const clearCitationHideTimer = () => {
    if (citationHideTimerRef.current) {
      clearTimeout(citationHideTimerRef.current);
      citationHideTimerRef.current = null;
    }
  };

  const showCitationForPill = (pill) => {
    const citation = citations.find(
      ({ number }) => String(number) === pill.dataset.citationNumber
    );
    if (!citation) {
      return;
    }

    clearCitationHideTimer();
    setActiveCitation({ citation, anchorRect: pill.getBoundingClientRect() });
  };

  const scheduleCitationHide = () => {
    clearCitationHideTimer();
    citationHideTimerRef.current = setTimeout(
      () => setActiveCitation(null),
      CITATION_HIDE_DELAY_MS
    );
  };

  const handleOpenCitationDocument = (citation) => {
    clearCitationHideTimer();
    setActiveCitation(null);
    if (!citation?.documentUrl) {
      return;
    }

    // The web app's PDF viewer opens at the cited page with the passage highlighted.
    if (isPdfCitation(citation) && getHostAppOrigin()) {
      props.onOpenCitation?.(citation);

      return;
    }

    openDocumentPreview(
      buildPreviewFileFromLink(citation.documentUrl, citation.documentName),
      setPreviewDocument
    );
  };

  useEffect(() => clearCitationHideTimer, []);

  // A tapped pill has no mouse-leave on touch screens, and the card is placed
  // once, so close it on a press outside, Escape, or when the chat scrolls.
  useEffect(() => {
    if (!activeCitation) {
      return undefined;
    }

    const closeCitation = () => setActiveCitation(null);
    const handlePointerDown = (event) => {
      const target = event.target;
      const isInsideCitation =
        target?.closest?.(".citation-popover") || target?.closest?.(`.${CITATION_PILL_CLASS}`);
      if (!isInsideCitation) {
        closeCitation();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeCitation();
      }
    };
    const handleScroll = (event) => {
      if (!event.target?.closest?.(".citation-popover")) {
        closeCitation();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [activeCitation]);

  const avatarName =
    typeof message?.name === "string" && message.name.trim()
      ? message.name.trim()
      : message?.type === "user"
      ? "You"
      : "Addy AI";
  const avatarInitial = avatarName[0]?.toUpperCase() || (message?.type === "user" ? "U" : "A");
  const shouldRenderAvatarImage = normalizedMessageImageUrl && !avatarImageFailed;

  useEffect(() => {
    setAvatarImageFailed(false);
  }, [messageImageUrl]);

  const extractYouTubeEmbeds = (text) => {
    if (!text || typeof text !== "string") return [];

    const matches = new Set();
    const patterns = [
      /https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11})/gi,
      /https?:\/\/(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]{11})/gi,
      /https?:\/\/(?:www\.)?youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/gi,
    ];

    patterns.forEach((pattern) => {
      let match;
      while ((match = pattern.exec(text)) !== null) {
        if (match[1]) {
          matches.add(`https://www.youtube.com/embed/${match[1]}`);
        }
      }
    });

    return Array.from(matches);
  };

  /**
   * Formats a message timestamp exactly like OpenAI's chat UI:
   *   Today 4:11 PM
   *   Yesterday 4:11 PM
   *   Feb 19, 4:11 PM
   */
  const formatMessageTimestamp = (timestamp) => {
    if (!timestamp) return null;
    const date = new Date(timestamp);
    const now = new Date();

    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    const isToday =
      date.getDate() === now.getDate() &&
      date.getMonth() === now.getMonth() &&
      date.getFullYear() === now.getFullYear();

    if (isToday) return `Today ${timeStr}`;

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday =
      date.getDate() === yesterday.getDate() &&
      date.getMonth() === yesterday.getMonth() &&
      date.getFullYear() === yesterday.getFullYear();

    if (isYesterday) return `Yesterday ${timeStr}`;

    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
    return `${dateStr}, ${timeStr}`;
  };
  const documentsCompared = props.documentsCompared;
  const clientID = props.clientID;
  const isLastMessage = props.isLastMessage;
  const isFirstMessage = props.isFirstMessage;
  const loadingDocuments = props.loadingDocuments;
  const thinkingStatements = props.thinkingStatements
    ? props.thinkingStatements
    : [""];
  const overlayMode = props.overlayMode;
  const statusBanner = props.status;
  const hideSkeleton = props.hideSkeleton;
  // Create a shuffled array of indexes for the thinking statements
  const [shuffledIndexes, setShuffledIndexes] = useState(
    shuffleArray([...Array(thinkingStatements.length).keys()])
  );

  const [currentIndex, setCurrentIndex] = useState(0);

  // Pick a random statement from the shuffled array
  useEffect(() => {
    let interval;
    if (
      message.action === "thinking" &&
      shuffledIndexes.length > 1 &&
      props.compareMode &&
      props.compareMode === true &&
      message.type === "assistant" &&
      message?.message?.length === 0
    ) {
      interval = setInterval(() => {
        setCurrentIndex((prevIndex) => {
          const nextIndex = prevIndex + 1;
          // If we've shown all statements, re-shuffle and reset to 0
          if (nextIndex >= shuffledIndexes.length) {
            setShuffledIndexes(
              shuffleArray([...Array(thinkingStatements.length).keys()])
            );
            return 0;
          }
          return nextIndex;
        });
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [message.action, message.message, shuffledIndexes]);

  const displayedThinkingMessage =
    thinkingStatements[shuffledIndexes[currentIndex]];
  const showTypingCursor = props.showTypingCursor;
  const activeStatusMessage = statusBanner?.message || "";
  const streamingStatusLabel =
    activeStatusMessage || displayedThinkingMessage || "Thinking...";
  const shouldShowThinkingStatus =
    showTypingCursor &&
    message.type === "assistant" &&
    message.action === "thinking";
  const shouldShowWritingDots =
    showTypingCursor &&
    message.type === "assistant" &&
    message.action !== "thinking";
  const shouldShowStatusBanner =
    isLastMessage &&
    message.type === "assistant" &&
    statusBanner?.message &&
    !showTypingCursor;
  const shouldHideIncompleteStreamingLinks = showTypingCursor;
  const shouldRenderYouTubeEmbeds =
    message.type === "assistant" && message.action !== "thinking";
  const youtubeEmbeds = shouldRenderYouTubeEmbeds
    ? extractYouTubeEmbeds(message?.message || "")
    : [];
  const responseTextClassNames = ["model-response-text", message.action == "thinking" ? "hide" : ""]
    .filter(Boolean)
    .join(" ");
  const renderStreamingStatus = ({ showLabel = true } = {}) => (
    <div
      className={`thinking-status ${showLabel ? "" : "thinking-status-dots-only"}`}
      role="status"
      aria-live="polite"
      aria-label={showLabel ? undefined : "Addy is still writing"}
    >
      <span className="thinking-dots" aria-hidden="true">
        <span />
        <span />
        <span />
      </span>
      {showLabel && <span className="thinking-text">{streamingStatusLabel}</span>}
    </div>
  );

  // const handleLikeClick = () => {
  //     setLikeSrc("/thumbs-up-filled.png");
  //     setDislikeSrc("/thumb-1.svg");
  // };

  const handleDislikeClick = () => {
    setDislikeSrc("/thumbs-down-filled.png");
    // setLikeSrc("/thumb.svg");
  };

  const STREAMING_LINK_PLACEHOLDER_TEXT = "Preparing secure link...";

  const getMarkedDownText = (markdownText, hideIncompleteStreamingLinks = false) => {
    // During streaming, hide incomplete markdown links to prevent showing raw URLs
    // Pattern: [text](url) - incomplete if missing closing )
    let processedText = markdownText;

    // Resolve short link references (addylink:N) using the map streamed via
    // the SSE `links` event. References without a URL yet render as their
    // label text (placeholder) and upgrade in place once the map arrives.
    processedText = resolveLinkReferences(processedText, props.linkMap);

    // Repair malformed citation clusters ([5[6]]) so each becomes its own pill.
    if (citations.length > 0) {
      processedText = normalizeCitationMarkers(processedText);
    }

    if (hideIncompleteStreamingLinks) {
      // Find incomplete markdown links at the end of text: [text](url without closing )
      // Only match if the text ends with an incomplete link (no closing parenthesis)
      // This prevents users from seeing raw URLs being typed out character by character
      // Pattern matches: [text](url... where url may be empty or partial, but no closing )
      const incompleteLinkPattern = /\[([^\]]+)\]\(([^)]*)$/;
      const lastMatch = processedText.match(incompleteLinkPattern);
      if (lastMatch && lastMatch.index + lastMatch[0].length === processedText.length) {
        // Replace incomplete link with just the text part (no URL shown)
        processedText = processedText.replace(
          incompleteLinkPattern,
          (match, linkText) => {
            // Show progress without leaking the incomplete href.
            return `${linkText} (${STREAMING_LINK_PLACEHOLDER_TEXT})`;
          }
        );
      }
    }

    const rawMarkup = marked(processedText, { sanitize: true });

    // Here marked() may have converted \n into \\n
    // so we replace \\n with <br />
    const lineBreaksAdded = rawMarkup.replace(
      /\\n/g,
      "<br class='addy-line-break' />"
    );

    // Purify the raw markup to prevent XSS attacks. The agent's action links use
    // custom schemes (navigate:, guide:) that DOMPurify's default allow-list would
    // strip, leaving dead buttons, so those two schemes are allowed explicitly.
    const purifiedMarkup = DOMPurify.sanitize(lineBreaksAdded, {
      ALLOWED_URI_REGEXP: AGENT_LINK_URI_REGEXP,
    });

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = purifiedMarkup;
    
  // Backstop for the resolver's own labelling: a raw URL must never survive as
  // visible link text, including one the model wrote directly without ever
  // passing through an addylink ref.
  const MAX_VISIBLE_LINK_TEXT = 80;

    // Process anchor tags
    tempDiv.querySelectorAll("a").forEach((anchor) => {
      const href = anchor.getAttribute("href") || "";
      const visibleText = anchor.textContent?.trim() || "";
      if (!visibleText) {
        anchor.textContent = "Click here";
      } else if (
        /^https?:\/\//i.test(visibleText) &&
        visibleText.length > MAX_VISIBLE_LINK_TEXT
      ) {
        anchor.textContent = deriveLinkLabelFromUrl(href || visibleText);
      }

      if (href.startsWith(NAVIGATE_LINK_SCHEME)) {
        // Deep-link navigation — handled by onClick, not by browser navigation
        anchor.setAttribute("href", href); // keep as-is for onClick detection
        anchor.removeAttribute("target");
        anchor.style.cssText =
          "display:inline-flex;align-items:center;gap:4px;padding:5px 12px;" +
          "background:var(--primary-color);color:#fff;border-radius:16px;font-size:13px;" +
          "font-weight:500;text-decoration:none;cursor:pointer;margin-top:4px;";
      } else if (isGuideHref(href)) {
        // "Show me on screen": starts the guided cursor in the hosting app (onClick).
        anchor.setAttribute("href", href);
        anchor.removeAttribute("target");
        anchor.classList.add(GUIDE_BUTTON_CLASS);
        anchor.setAttribute("title", "Guide me through this on screen");
      } else if (canPreviewDocumentLink(href, anchor.textContent?.trim())) {
        anchor.classList.add("document-preview-link");
        anchor.removeAttribute("target");
        anchor.removeAttribute("rel");
      } else {
        anchor.setAttribute("target", "_blank");
        anchor.setAttribute("rel", "noopener noreferrer");
      }
    });

    // A finished answer leads with "Show me on screen" instead of ending with it.
    placeGuideButtons(tempDiv, {
      moveToTop: !hideIncompleteStreamingLinks,
      available: guideAvailable,
    });

    // Wrap tables in a scrollable container for horizontal scrolling
    // This allows tables to scroll independently without affecting the entire message
    tempDiv.querySelectorAll("table").forEach((table) => {
      const wrapper = document.createElement("div");
      wrapper.className = "table-scroll-wrapper";
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });

    // [N] markers with a citation become clickable source pills (DEV-6130).
    insertCitationPills(tempDiv, citations);

    return { __html: tempDiv.innerHTML };
  };

  const getCleanMessageText = () => {
    // Exports leave out the citation markers the pills replace on screen.
    return stripCitationMarkers(message?.message || "", citations).replace(/\\n/g, "\n").trim();
  };

  const getDisplayMessageText = () => {
    const rawMessage = message?.message || "";
    if (!pricingResults) return rawMessage;

    const sectionStarts = [
      rawMessage.indexOf("\n## Scenario used"),
      rawMessage.indexOf("\n## Top pricing results"),
      rawMessage.indexOf("\n| # | Investor"),
    ].filter((index) => index >= 0);

    if (!sectionStarts.length) return rawMessage;
    return rawMessage.slice(0, Math.min(...sectionStarts)).trim();
  };

  const getExportFilename = () => {
    const timestamp = new Date()
      .toISOString()
      .slice(0, 16)
      .replace("T", "-")
      .replace(":", "-");
    return `ask-addy-response-${timestamp}`;
  };

  const downloadBlob = (content, filename, mimeType) => {
    const blobParts = Array.isArray(content) ? content : [content];
    const blob = new Blob(blobParts, { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadBase64File = (base64Content, filename, mimeType) => {
    const binaryString = window.atob(base64Content);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i += 1) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    downloadBlob([bytes], filename, mimeType);
  };

  const downloadExportFile = async (format) => {
    const backendFormat = format === "word" ? "docx" : "pdf";
    // Ask Addy export renders from the response text alone. Loan context is
    // optional (absent in the full-screen modal and Help Center), so we only
    // include organizationId/loanId when they are available.
    const requestBody = {
      rawResponseText: getCleanMessageText(),
      exportSource: "askAddy",
      filenameBase: getExportFilename(),
      format: backendFormat,
    };
    if (props.organizationId) requestBody.organizationId = props.organizationId;
    if (props.loanId) requestBody.loanId = props.loanId;

    const response = await fetch(
      `${GlobalVariables.getAPIURL()}/api/loan/export-processing-checklist-document`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );
    const data = await response.json();

    if (!response.ok || !data?.success) {
      throw new Error(data?.error || `Failed to export Ask Addy response as ${format}`);
    }

    downloadBase64File(
      data.fileData,
      data.fileName || `${getExportFilename()}.${backendFormat}`,
      data.mimeType ||
        (backendFormat === "docx"
          ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          : "application/pdf")
    );
  };

  const handleExportMenuOpen = (event) => {
    if (exportingFormat) return;
    setExportMenuAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchorEl(null);
  };

  const handleExportClick = async (format) => {
    if (!getCleanMessageText() || exportingFormat) return;

    setExportingFormat(format);
    setExportError(false);
    handleExportMenuClose();

    try {
      await downloadExportFile(format);
    } catch (error) {
      console.error(`Error exporting Ask Addy response as ${format}:`, error);
      setExportError(true);
      setTimeout(() => setExportError(false), 5000);
    } finally {
      setExportingFormat(null);
    }
  };

  const handleCopyClick = async () => {
    setCopySucceeded(true);
    setTimeout(() => {
      setCopySucceeded(false);
    }, 5000);

    const node = responseTextRef.current;
    const rawText = message?.message?.replace(/\\n/g, "\n") || "";
    // Clean, visible-only text (document names, no raw signed URLs) — used both
    // for the webpage mirror (e.g. Gmail extension) and the plain-text fallback.
    const cleanText = getRenderedPlainText(node) || rawText;

    // Mirror the copy action to the parent webpage (the Gmail extension writes
    // this to the clipboard itself). Send clean text so no raw URL leaks there.
    AddyWebSocket.sendDataToWebsocket(
      cleanText,
      `${clientID}-webpage`,
      "copy",
      3
    );

    // In the web app, copy the rendered content: rich HTML keeps document names
    // as clean clickable links, matching a manual cursor copy/paste.
    const didCopy = await copyRenderedMessage(node);
    if (didCopy) {
      return;
    }

    try {
      await navigator.clipboard.writeText(cleanText);
    } catch (err) {
      console.error("Error copying text to clipboard: ", err);
    }
  };

  const handleInsertClick = () => {
    // console.log("Insert clicked");
    // Send insert action to client via WebSocket
    const copiedText = message?.message?.replace(/\\n/g, "\n");
    AddyWebSocket.sendDataToWebsocket(
      copiedText,
      `${clientID}-webpage`,
      "insertTextInReplyEditor"
    );
  };

  const isValidURL = (str) => {
    try {
      new URL(str);
    } catch (_) {
      return false;
    }
    return true;
  };

  const toggleExpand = (index) => {
    setExpandedSourceIndexes((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  // Append 'http://' if protocol not present
  const appendHttp = (url) => {
    if (!/^(?:f|ht)tps?\:\/\//.test(url)) {
      url = "http://" + url;
    }
    return url;
  };

  const getFaviconURL = (websiteURL) => {
    if (!websiteURL) {
      return "";
    }
    try {
      const urlObject = new URL(websiteURL);
      // Fetch url
      return `https://www.google.com/s2/favicons?domain=${urlObject.hostname}`;
    } catch (_) {
      return "";
    }
  };

  const sourceComponent = (documentTitle, documentContent, referenceNumber) => {
    // Check if the document title is a URL or not
    let isDocumentTitleAURL = documentTitle ? true : false;
    if (documentTitle && !isValidURL(documentTitle)) {
      const testAppendProtocol = appendHttp(documentTitle);
      if (!isValidURL(testAppendProtocol)) {
        // Still not a url
        isDocumentTitleAURL = false;
      }
    }
    return (
      <div
        className="source-container"
        onClick={() => {
          windowWidth > 850 &&
            isDocumentTitleAURL
            // window.open(documentTitle, "_blank");
        }}
      >
        <div className="source-container-header">
          {/* The favicon if available */}
          {windowWidth > 850 &&
            isDocumentTitleAURL &&
            getFaviconURL(documentTitle) && (
              <img
                className="source-website-favicon"
                src={getFaviconURL(documentTitle)}
                alt=""
              />
            )}

          {/* {windowWidth > 850 && (
       
          )} */}
          <div className="source-container-link">{documentTitle}</div>
          {/* {windowWidth > 850 && (
            <div className="source-delimiter-symbol">&nbsp;•&nbsp;</div>
          )}

          <div className="source-reference-number">{referenceNumber}</div> */}
        </div>

        {/* <div className="source-content-text">
            {documentContent}
        </div> */}
      </div>
    );
  };

  const formatTextWithLineBreaks = (text) => {
    if (!text) {
      return "";
    }
    
    // Be very conservative - only handle clear line break patterns
    // Split on: escaped newlines, space+n+capital/number/bullet, or n at start+capital/number/bullet
    const lines = text.split(/\\n|\s+n(?=[A-Z0-9(■])|^n(?=[A-Z0-9(■])/);
    
    return lines.map((line, index) => (
      <span key={index}>
        {line.trim()}
        {index < lines.length - 1 && <br />}
      </span>
    ));
  };

  // Some document citations arrive as a raw JSON blob (e.g. OCR key/value
  // extractions like {"keyValuePairs":[{"key":"SSN","value":"..."}]}). Parse
  // those into a clean, human-readable excerpt instead of showing raw JSON.
  const parseJsonLike = (str) => {
    if (typeof str !== "string" || !str.trim()) return undefined;
    try {
      return JSON.parse(str);
    } catch (e) {
      // Fall through to extracting an embedded JSON object/array.
    }
    const match = str.match(/[[{][\s\S]*[\]}]/);
    if (match) {
      try {
        return JSON.parse(match[0]);
      } catch (e) {
        return undefined;
      }
    }
    return undefined;
  };

  const TEXT_FIELDS = ["text", "content", "value", "markdown", "rawText", "excerpt"];

  const jsonToReadableLines = (node) => {
    const lines = [];
    const walk = (n) => {
      if (n === null || n === undefined) return;
      if (typeof n === "string") {
        const s = n.trim();
        if (s) lines.push(s);
        return;
      }
      if (typeof n === "number" || typeof n === "boolean") {
        lines.push(String(n));
        return;
      }
      if (Array.isArray(n)) {
        const isKeyValueArray = n.every(
          (item) =>
            item && typeof item === "object" && "key" in item && "value" in item
        );
        if (isKeyValueArray) {
          n.forEach((item) => {
            const k = String(item.key ?? "").trim();
            const v = String(item.value ?? "").trim();
            if (k || v) lines.push(k ? `${k}: ${v}` : v);
          });
          return;
        }
        n.forEach(walk);
        return;
      }
      if (typeof n === "object") {
        if (Array.isArray(n.keyValuePairs)) walk(n.keyValuePairs);
        TEXT_FIELDS.forEach((field) => {
          if (typeof n[field] === "string" && n[field].trim()) {
            lines.push(n[field].trim());
          }
        });
        Object.keys(n).forEach((key) => {
          if (key === "keyValuePairs" || TEXT_FIELDS.includes(key)) return;
          const val = n[key];
          if (Array.isArray(val) || (val && typeof val === "object")) walk(val);
        });
      }
    };
    walk(node);
    return lines;
  };

  const cleanReferenceExcerpt = (raw) => {
    if (raw === null || raw === undefined) return "";
    const asString = typeof raw === "string" ? raw : JSON.stringify(raw);
    const parsed =
      typeof raw === "string" ? parseJsonLike(raw.trim()) : raw;
    if (parsed !== undefined && parsed !== null && typeof parsed === "object") {
      const readable = jsonToReadableLines(parsed).join("\n").trim();
      if (readable) return readable;
    }
    return asString;
  };

  const renderReferenceExcerpt = (raw) => {
    const cleaned = cleanReferenceExcerpt(raw);
    const lines = cleaned.split(/\r?\n/).filter((line) => line.trim().length > 0);
    if (lines.length === 0) return null;
    return lines.map((line, index) => (
      <span key={index}>
        {line.trim()}
        {index < lines.length - 1 && <br />}
      </span>
    ));
  };

  const CustomTooltip = styled(({ className, open, ...props }) => (
    <Tooltip
      {...props}
      arrow
      classes={{ popper: className }}
      open={open}
      onClose={() => setOpenTooltipIndex(null)}
    />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "var(--surface-card)",
      color: "var(--text-primary)",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
      fontSize: "14px",
      borderRadius: "8px",
      padding: "8px 16px",
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: "var(--surface-card)",
    },
  }));

  const sourceCard = ({ title, text, sources, reasoning }) => {
    return (
      <div className="comparison-card">
        <dev className="comparison-title">{title}</dev>
        <div className="comparison-content">{formatTextWithLineBreaks(text)}</div>

        {sources && sources.length > 0 && (
          <div>
            <div className="comparison-index">Sources</div>
            <div className="source-buttons">
              {sources.map((source, index) => (
                <CustomTooltip
                  title={sourceTooltipComponent(source, index + 1)}
                >
                  <div className="source-button">{index + 1}</div>
                </CustomTooltip>
              ))}
            </div>
          </div>
        )}

        {/* {reasoning && (
          <div>
            <div className="comparison-index">Reasoning</div>
            <div className="source-buttons">
              <CustomTooltip
                title={comparisonTooltipComponent(
                  { text: reasoning, fullText: reasoning },
                  1
                )}
              >
                <div className="source-button">{1}</div>
              </CustomTooltip>
            </div>
          </div>
        )} */}
      </div>
    );
  };

  const sourceTooltipComponent = (source, index) => {
    return (
      <div className="tooltip-content">
        <img
          className="close-tooltip-icon"
          onClick={() => setOpenTooltipIndex(null)}
          loading="lazy"
          alt=""
          src="/close.svg"
        />
        <div className="tooltip-title">{source.title}</div>
        <div className="tooltip-text">
          {expandedSourceIndexes[index]
            ? renderReferenceExcerpt(source.fullText)
            : renderReferenceExcerpt(source.text)}
        </div>
        <div
          className="tooltip-footer"
          onClick={(e) => {
            e.stopPropagation();
            toggleExpand(index);
          }}
          style={{ cursor: "pointer", color: "blue" }}
        >
          {expandedSourceIndexes[index] ? "View less" : "View more"}
        </div>
      </div>
    );
  };

  const ComparisonCard = ({ title, text, sources, reasoning, path }) => {
    return (
      <div className="comparison-card">
        {path && path.split(" > ").length > 1 ? (
          <CustomTooltip title={path}>
            <div className="comparison-title">
              {(() => {
                const pathSegments = path
                  .split(" > ")
                  .map((segment) => segment.trim());
                const targetIndex = pathSegments[1]
                  ?.toLowerCase()
                  .includes("mortgage insurers")
                  ? 2
                  : 1;
                return pathSegments[targetIndex]
                  ? pathSegments[targetIndex].charAt(0).toUpperCase() +
                      pathSegments[targetIndex].slice(1)
                  : title;
              })()}
            </div>
          </CustomTooltip>
        ) : (
          <div className="comparison-title">{title}</div>
        )}

        <div className="comparison-content">{formatTextWithLineBreaks(text)}</div>

        {sources && sources.length > 0 && (
          <div>
            <div className="comparison-index">Sources</div>
            <div className="source-buttons">
              {sources.map((source, index) => (
                <CustomTooltip
                  title={comparisonTooltipComponent(source, title)}
                >
                  <div className="source-button">{index + 1}</div>
                </CustomTooltip>
              ))}
            </div>
          </div>
        )}

        {/* {reasoning && (
            <div className="comparison-section">
              <div className="comparison-index">Reasoning</div>
              <div className="source-buttons">
                <CustomTooltip
                  title={comparisonTooltipComponent(reasoning, title)}
                >
                  <div className="source-button">1</div>
                </CustomTooltip>
              </div>
            </div>
          )} */}
      </div>
    );
  };

  const comparisonTooltipComponent = (source, index) => {
    return (
      <div className="tooltip-content">
        <div className="tooltip-title">{index}</div>
        <div className="tooltip-text">{formatTextWithLineBreaks(source.text)}</div>

        <div className="tooltip-footer">
          <a href={source.url} target="_blank" rel="noopener noreferrer">
            View source
          </a>
        </div>
      </div>
    );
  };

  useEffect(() => {
    // console.log("i am here in feedback message", feedbackContainerRef.current);
    if (feedbackContainerRef.current && !isLastMessage) {
      //   console.log("i am here in feedback message 222");
      feedbackContainerRef.current.addEventListener("mouseenter", () => {
        // console.log("i am here in mouse enter");
        feedbackContainerRef.current.classList.add("show-message-actions");
      });
      feedbackContainerRef.current.addEventListener("mouseleave", () => {
        feedbackContainerRef.current.classList.remove("show-message-actions");
      });
    }
    return () => {
      if (feedbackContainerRef.current) {
        feedbackContainerRef.current.removeEventListener(
          "mouseenter",
          () => {}
        );
        feedbackContainerRef.current.removeEventListener(
          "mouseleave",
          () => {}
        );
      }
    };
  }, [isLastMessage]);

  return (
    <section
      className={`top-bar-new-message5 ${
        message.type == "user"
          ? "user-message"
          : message.type == "assistant"
          ? "assistant-message"
          : ""
      } ${isFirstMessage ? "first-message" : ""} ${
        isLastMessage ? "last-message" : ""
      } ${overlayMode ? "message-text-overlay-mode" : ""}`}
      id={message.messageId ? message.messageId : uuidv4()}
    >
      {props?.usedFiles?.length > 0 && (
        <div className="mb-[12px]" style={{marginBottom: "12px"}}>
          <AttachmentContainer files={props?.usedFiles?.map((file) => file?.files)} setFiles={() => {}} isViewingUsedFiles={true} />
        </div>
      )}
      <div className="text-field5">
        <div className="h-heading-group">
          <div className="scroll-bar5">
            <div className="image-placeholder1">
              <div className="wrapper-union">
                {shouldRenderAvatarImage ? (
                  <img
                    className="union-icon"
                    loading="lazy"
                    style={{
                      objectFit: "cover",
                    }}
                    alt={`${message.type}`}
                    src={normalizedMessageImageUrl}
                    onError={() => setAvatarImageFailed(true)}
                  />
                ) : message.type === "assistant" ? (
                  <img
                    className="union-icon assistant-avatar-fallback-icon"
                    loading="lazy"
                    alt=""
                    src="/assistant-icon.svg"
                  />
                ) : (
                  <span className="message-avatar-initial" aria-label={avatarName}>
                    {avatarInitial}
                  </span>
                )}
              </div>
            </div>

            <div className="frame-parent1">
              <div className="h2-heading-parent24">
                <div className="message-name-row">
                  <b className="h2-assistant-name">
                    {message.name && message.name.length > 40
                      ? `${message.name.substring(0, 39)}...`
                      : message.name}
                  </b>
                  {/* Timestamp shown for completed assistant messages only */}
                  {message.type === "assistant" &&
                    message.action !== "thinking" &&
                    message.timestamp && (
                      <span className="message-timestamp">
                        {formatMessageTimestamp(message.timestamp)}
                      </span>
                    )}
                </div>
                {message.type === "user" && message.selectedText && (
                  <div className="selected-text-parent">
                    <div className="selected-text-header-parent">
                      <div className="selected-text-header">
                        {message.selectedTextTitle || "Selected text"}
                      </div>
                    </div>
                    <div className="selected-text">
                      {message.selectedText.length > 250
                        ? message.selectedText.substring(0, 250) + "..."
                        : message.selectedText}
                    </div>
                  </div>
                )}
                {documentsCompared && documentsCompared.length > 0 && (
                  <div style={{ marginBottom: "20px" }}>
                    <div className="comparison-list">
                      <div className="comparison-list-front">
                        <img
                          className="addy-check-security tooltip-1"
                          width="16"
                          height="16"
                          src="/book.png"
                        />
                        <div className="comparison-title">
                          Investor Guidelines Comparison
                        </div>
                      </div>
                      <div
                        className="comparison-list-end"
                        onClick={() => setOpenDialog(true)}
                      >
                        <div className="comparison-number">
                          {documentsCompared.length - 3 > 0 &&
                            (documentsCompared.length - 3 < 5
                              ? `+${documentsCompared.length - 3}`
                              : `5+`)}
                        </div>
                        <div
                          style={{
                            textDecoration: "underline",
                            cursor: "pointer",
                          }}
                        >
                          View all
                        </div>
                      </div>
                    </div>
                    <div className="sources-comparison-list addy-custom-scroll">
                      {documentsCompared.map(
                        (context, index) =>
                          index < 3 && (
                            <ComparisonCard
                              title={context.title}
                              text={context.text}
                              sources={context.source}
                              reasoning={context.reasoning}
                              path={context.filePath}
                            />
                          )
                      )}
                    </div>
                  </div>
                )}
                {documents && documents.length > 0 ? (
                  <div className="sources-parent-div">
                    <div className="sources-title">Sources</div>
                    <div className="sources-list addy-custom-scroll">
                      {documents.map((context, index) =>
                        windowWidth > 850 ? (
                          <>
                            <CustomTooltip
                              key={index}
                              title={sourceTooltipComponent(context, index + 1)}
                              open={openTooltipIndex === index}
                            >
                              <div
                                onMouseEnter={() => setOpenTooltipIndex(index)}
                              >
                                {(index + 1 <= 3 || showMoreSources) &&
                                  sourceComponent(
                                    context.title,
                                    context.text,
                                    index + 1
                                  )}
                              </div>
                            </CustomTooltip>
                          </>
                        ) : (
                          <>
                            <CustomTooltip
                              key={index}
                              title={sourceTooltipComponent(context, index + 1)}
                              open={openTooltipIndex === index}
                            >
                              <div
                                onClick={() => {
                                  setOpenTooltipIndex((prev) =>
                                    prev === index ? null : index
                                  );
                                }}
                              >
                                {(index + 1 <= 3 || showMoreSources) &&
                                  sourceComponent(
                                    context.title,
                                    context.text,
                                    index + 1
                                  )}
                              </div>
                            </CustomTooltip>
                          </>
                        )
                      )}
                    </div>
                    {documents && documents.length > 3 && windowWidth > 850 && (
                      <div
                        className="show-more-sources-text"
                        onClick={() => {
                          setShowMoreSources(!showMoreSources);
                        }}
                      >
                        {showMoreSources
                          ? `Hide ${documents.length - 3} sources`
                          : `Show ${documents.length - 3} more`}
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Fallback to loading skeleton when loading documents */}
                    {isLastMessage &&
                      loadingDocuments &&
                      !hideSkeleton &&
                      message.type !== "user" && (
                        <div>
                          <div className="sources-title">Sources</div>
                          <div
                            className="sources-list addy-custom-scroll"
                            style={{ marginBottom: "20px" }}
                          >
                            <Skeleton variant="rounded" width={300} height={37} />
                            <Skeleton variant="rounded" width={300} height={37} />
                            <Skeleton variant="rounded" width={300} height={37} />
                          </div>
                        </div>
                      )}
                  </>
                )}

                <div className="h2-heading50">
                  <span>
                    {shouldShowStatusBanner && (
                      <div className="agent-trace-message">
                        {statusBanner.message}
                      </div>
                    )}

                    {/* eslint-disable-next-line max-len */}
                    {/* <div className={`model-response-text ${message.action == "thinking" ? "hide" : ""}`}>
                                            {parse(message.message)}
                                        </div> */}
                    <div
                      ref={responseTextRef}
                      className={responseTextClassNames}
                      dangerouslySetInnerHTML={getMarkedDownText(
                        getDisplayMessageText(),
                        shouldHideIncompleteStreamingLinks &&
                          message.action !== "thinking"
                      )}
                      onMouseOver={(e) => {
                        const pill = e.target.closest?.(`.${CITATION_PILL_CLASS}`);
                        if (pill) {
                          showCitationForPill(pill);
                        }
                      }}
                      onMouseOut={(e) => {
                        if (e.target.closest?.(`.${CITATION_PILL_CLASS}`)) {
                          scheduleCitationHide();
                        }
                      }}
                      onClick={(e) => {
                        const pill = e.target.closest(`.${CITATION_PILL_CLASS}`);
                        if (pill) {
                          e.preventDefault();
                          showCitationForPill(pill);

                          return;
                        }

                        const anchor = e.target.closest("a");
                        if (!anchor) {
                          return;
                        }

                        const href = anchor.getAttribute("href") || "";
                        if (href.startsWith(NAVIGATE_LINK_SCHEME)) {
                          e.preventDefault();
                          const path = parseNavigateHref(href);
                          if (!path) {
                            return;
                          }
                          navigateFromLink({
                            path,
                            parentWindow:
                              window.parent && window.parent !== window ? window.parent : null,
                            currentURL: new URLSearchParams(window.location.search).get(
                              "currentURL"
                            ),
                          });

                          return;
                        }

                        if (isGuideHref(href)) {
                          e.preventDefault();
                          const walkthroughId = parseGuideHref(href);
                          if (!walkthroughId || anchor.classList.contains("agent-guide-link-pending")) {
                            return;
                          }
                          // The button is shown twice (top and bottom): hold both while it starts.
                          const guideButtons = Array.from(
                            e.currentTarget.querySelectorAll(`a.${GUIDE_BUTTON_CLASS}`)
                          );
                          guideButtons.forEach((button) =>
                            button.classList.add("agent-guide-link-pending")
                          );
                          const hostWindow =
                            window.parent && window.parent !== window ? window.parent : null;
                          const currentURL = new URLSearchParams(window.location.search).get(
                            "currentURL"
                          );
                          startGuidedWalkthroughFromLink({
                            walkthroughId,
                            goal: props.previousUserMessage || "",
                            articleUrl: findHelpArticleUrl(
                              anchor.closest(".model-response-text") || e.currentTarget
                            ),
                            parentWindow: hostWindow,
                            currentURL,
                          }).finally(() => {
                            guideButtons.forEach((button) =>
                              button.classList.remove("agent-guide-link-pending")
                            );
                          });

                          return;
                        }

                        if (canPreviewDocumentLink(href, anchor.textContent?.trim())) {
                          e.preventDefault();
                          openDocumentPreview(
                            buildPreviewFileFromLink(href, anchor.textContent?.trim()),
                            setPreviewDocument
                          );
                        }
                      }}
                    />
                    {activeCitation && (
                      <CitationPopover
                        citation={activeCitation.citation}
                        anchorRect={activeCitation.anchorRect}
                        onOpenDocument={handleOpenCitationDocument}
                        onMouseEnter={clearCitationHideTimer}
                        onMouseLeave={scheduleCitationHide}
                      />
                    )}
                    {pricingResults && (
                      <PricingResultsCard pricingResults={pricingResults} />
                    )}
                    {youtubeEmbeds.length > 0 && (
                      <div className="youtube-embed-container">
                        {youtubeEmbeds.map((embedUrl, index) => (
                          <div className="youtube-embed-frame-wrapper" key={`${embedUrl}-${index}`}>
                            <iframe
                              className="youtube-embed-frame"
                              src={embedUrl}
                              title={`YouTube video ${index + 1}`}
                              loading="lazy"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                              referrerPolicy="strict-origin-when-cross-origin"
                              allowFullScreen
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    {shouldShowThinkingStatus && renderStreamingStatus()}
                    {shouldShowWritingDots &&
                      renderStreamingStatus({ showLabel: false })}

                    {/* Clarification options are now displayed as markdown in the message content */}
                  </span>
                </div>
              </div>
              {message.type === "assistant" && (
                <div
                  ref={feedbackContainerRef}
                  className={`feedback-container frame-parent2 ${
                    (message.action == "thinking" && !message?.message?.length) ||
                    props.showTypingCursor
                      ? "hide"
                      : ""
                  } ${isLastMessage ? "show-message-actions" : ""}`}
                >
                  <div className="addy-copy-response-div">
                    {props.isEmail && (
                      <div
                        className="copy-response thumb-group"
                        onClick={handleInsertClick}
                      >
                        <img
                          className="thumb-icon2"
                          alt="insert"
                          src="/insert_stroke.svg"
                          data-toggle="tooltip"
                          title="Insert in email editor"
                        />
                        <div className="h2-heading52">Insert</div>
                      </div>
                    )}

                    <div
                      className="copy-response thumb-group"
                      onClick={handleCopyClick}
                    >
                      {copySucceeded ? (
                        <CheckIcon
                          className="thumb-icon2"
                          data-toggle="tooltip"
                          titleAccess="Copied"
                          sx={{ fontSize: 15, color: "var(--text-brand)" }}
                        />
                      ) : (
                        <ContentCopyOutlinedIcon
                          className="thumb-icon2"
                          data-toggle="tooltip"
                          titleAccess="Copy to clipboard"
                          sx={{ fontSize: 15, color: "var(--text-brand)" }}
                        />
                      )}
                      <div className="h2-heading52">Copy</div>
                    </div>

                    <div
                      className={`copy-response thumb-group ${
                        exportingFormat ? "export-response-disabled" : ""
                      }`}
                      onClick={handleExportMenuOpen}
                    >
                      {exportingFormat ? (
                        <CircularProgress
                          className="thumb-icon2"
                          size={13}
                          sx={{ color: "var(--text-brand)" }}
                        />
                      ) : (
                        <DescriptionOutlinedIcon
                          className="thumb-icon2"
                          sx={{ fontSize: 15, color: "var(--text-brand)" }}
                        />
                      )}
                      <div className="h2-heading52">
                        {exportingFormat ? "Exporting..." : "Export"}
                      </div>
                    </div>
                    <Menu
                      anchorEl={exportMenuAnchorEl}
                      open={Boolean(exportMenuAnchorEl)}
                      onClose={handleExportMenuClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                    >
                      <MenuItem
                        dense
                        disabled={Boolean(exportingFormat)}
                        onClick={() => handleExportClick("pdf")}
                      >
                        Download as PDF
                      </MenuItem>
                      <MenuItem
                        dense
                        disabled={Boolean(exportingFormat)}
                        onClick={() => handleExportClick("word")}
                      >
                        Download as Word
                      </MenuItem>
                    </Menu>

                    {exportError && (
                      <div
                        className="h2-heading52"
                        style={{ color: "var(--error-color, var(--danger-fg))" }}
                      >
                        Export failed. Please try again.
                      </div>
                    )}

                    {props.isEmail && (
                      <div
                        className="copy-response thumb-group"
                        onClick={props.onRephraseButtonClick}
                      >
                        <img
                          className="thumb-icon2"
                          alt="rephrase"
                          src="/rephrase.svg"
                          f
                          data-toggle="tooltip"
                          title="Rephrase the provided email"
                        />
                        <div className="h2-heading52">Rephrase</div>
                      </div>
                    )}
                  </div>

                  <div className="addy-rate-response-div">
                    {/* <div className="thumb-parent">
                                            <img
                                                data-toggle="tooltip"
                                                title="Good response"
                                                className="like-thumb thumb-icon"
                                                loading="lazy"
                                                alt=""
                                                src={likeSrc}
                                                onClick={handleLikeClick}
                                            />
                                            <div className="h2-heading51"></div>
                                        </div> */}
                    {/* <div className="thumb-parent">
                      <img
                        data-toggle="tooltip"
                        title="Bad response"
                        className="dislike-thumb thumb-icon"
                        alt=""
                        src={dislikeSrc}
                        onClick={handleDislikeClick}
                      />
                      <div className="h2-heading52"></div>
                    </div> */}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <ComparisonModal
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        documentsCompared={documentsCompared}
        message={message}
        clientID={clientID}
      />
      {previewDocument && (
        <FilePreview
          file={previewDocument}
          onClose={() => setPreviewDocument(null)}
        />
      )}
    </section>
  );
};

export default MessageText;
